import { useRef } from 'react'

// Keep per-node `data` objects referentially STABLE across renders so React
// Flow doesn't re-render every node on a box-select tick (ENG-1068). The graph
// editors wrap each node with handlers + debug fields via `nodes.map(n => ({
// ...n, data: {...} }))`, which minted a fresh `data` object for EVERY node on
// EVERY render. Panning didn't touch node state, so it never mattered — but a
// marquee flips `selected` on each pointermove, and the fresh-data-for-all map
// then re-rendered the entire graph per move, freezing the drag until release.
//
// React Flow's applyNodeChanges preserves the object identity of nodes that did
// not change, so we reuse a node's previous wrapper whenever its source object
// is the same AND none of the shared external inputs (`deps`) changed. The one
// node whose `selected` flipped gets a new source ref → a fresh wrapper → it
// (and only it) re-renders. When a shared input changes (e.g. the 100ms debug
// poll), the cache is dropped and everything rebuilds — exactly the old
// behaviour, just no longer on the per-pointermove path.
//
// `buildData(n)` returns the extra fields merged into `n.data`. `deps` is the
// array of external inputs compared shallowly (Object.is).
export function useStableNodeData(nodes, buildData, deps) {
  const ref = useRef({ cache: new Map(), deps: null })
  const prevDeps = ref.current.deps
  const depsChanged = !prevDeps
    || deps.length !== prevDeps.length
    || deps.some((d, i) => !Object.is(d, prevDeps[i]))
  if (depsChanged) { ref.current.cache = new Map(); ref.current.deps = deps }

  const cache = ref.current.cache
  const next = new Map()
  const out = nodes.map((n) => {
    const prev = cache.get(n.id)
    if (prev && prev.src === n) { next.set(n.id, prev); return prev.wrapped }
    const wrapped = { ...n, data: { ...n.data, ...buildData(n) } }
    next.set(n.id, { src: n, wrapped })
    return wrapped
  })
  ref.current.cache = next
  return out
}
